import React, { useState, useEffect } from 'react';
import { authService } from '../../services/auth.service';
import { subscriptionService } from '../../services/subscription.service';
import { User } from '../../types/api.types';
import { Sidebar, Header, ToastContainer } from '../Dashboard/DashboardComponents';
import {
    CreditCard,
    Calendar,
    ShieldCheck,
    ExternalLink,
    Loader2,
    AlertCircle,
    ArrowRight,
    CheckCircle2,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const Billing = () => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [portalLoading, setPortalLoading] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 1024);
            setSidebarOpen(window.innerWidth >= 1024);
        };
        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    useEffect(() => {
        const loadProfile = async () => {
            try {
                const data = await authService.getProfile();
                setUser(data);
            } catch {
                navigate('/login');
            } finally {
                setLoading(false);
            }
        };
        loadProfile();
    }, [navigate]);

    const handleLogout = () => {
        authService.logout();
        navigate('/login');
    };

    const handleManageSubscription = async () => {
        try {
            setPortalLoading(true);
            const portalUrl = await subscriptionService.getPortalUrl();
            window.location.href = portalUrl;
        } catch {
            setPortalLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex h-screen bg-slate-50 items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                    <span className="text-sm text-slate-500 font-medium">Loading profile...</span>
                </div>
            </div>
        );
    }

    const isTrial = user?.subscriptionStatus === 'trial';
    const isActive = user?.subscriptionStatus === 'active';
    const isExpired = !isTrial && !isActive;

    const trialDaysLeft = user?.trialEndsAt
        ? Math.max(0, Math.ceil((new Date(user.trialEndsAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
        : 0;

    const renewalDate = user?.renewsAt
        ? new Date(user.renewsAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        : null;

    const trialEndDate = user?.trialEndsAt
        ? new Date(user.trialEndsAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        : null;

    return (
        <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden font-sans">
            <Sidebar
                isOpen={sidebarOpen}
                isMobile={isMobile}
                user={user}
                onToggle={() => setSidebarOpen(!sidebarOpen)}
                activePage="billing"
            />
            <div className="flex-1 flex flex-col overflow-hidden relative z-10">
                <Header
                    isMobile={isMobile}
                    onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
                    user={user}
                    onLogout={handleLogout}
                    title="Subscription"
                />
                <ToastContainer />
                <main className="flex-1 overflow-y-auto custom-scrollbar p-6 lg:p-12">
                    <div className="max-w-4xl mx-auto space-y-10">

                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
                                Billing
                            </h2>
                            <p className="text-slate-500 text-sm mt-1">Manage your plan and payment methods</p>
                        </div>

                        {/* Status Card */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`rounded-3xl border p-8 lg:p-10 transition-all duration-300 shadow-sm ${isActive
                                ? 'bg-emerald-50 border-emerald-100'
                                : isTrial
                                    ? 'bg-white border-slate-200 shadow-xl shadow-indigo-100/20'
                                    : 'bg-amber-50 border-amber-100'
                                }`}
                        >
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
                                <div>
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${isActive ? 'bg-emerald-100 text-emerald-700' :
                                            isTrial ? 'bg-indigo-50 text-indigo-700' :
                                                'bg-amber-100 text-amber-700'
                                            }`}>
                                            {isActive ? 'Standard Pro' : isTrial ? 'Free Trial' : 'Subscription Ended'}
                                        </div>
                                        {isActive && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                                    </div>

                                    <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4">
                                        {isActive ? 'Pro Workspace' : isTrial ? `${trialDaysLeft} days left in your trial` : 'Account Paused'}
                                    </h2>

                                    <div className="space-y-2">
                                        {isTrial && trialEndDate && (
                                            <div className="flex items-center gap-2 text-slate-500 font-medium text-sm">
                                                <Calendar className="w-4 h-4 text-slate-400" />
                                                <span>Your free trial ends on <span className="text-slate-900 font-bold">{trialEndDate}</span></span>
                                            </div>
                                        )}
                                        {isActive && renewalDate && (
                                            <div className="flex items-center gap-2 text-slate-500 font-medium text-sm">
                                                <Calendar className="w-4 h-4 text-slate-400" />
                                                <span>Next renewal date: <span className="text-slate-900 font-bold">{renewalDate}</span></span>
                                            </div>
                                        )}
                                        {isExpired && (
                                            <div className="flex items-center gap-2 text-amber-700 font-medium text-sm">
                                                <AlertCircle className="w-4 h-4" />
                                                <span>Your trial has ended. Please subscribe to continue exporting.</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="shrink-0">
                                    {!isActive ? (
                                        <Link to="/pricing">
                                            <button className="bg-indigo-600 text-white hover:bg-indigo-700 px-8 h-12 rounded-xl font-bold text-sm transition-all shadow-md shadow-indigo-100 flex items-center justify-center min-w-[180px]">
                                                Upgrade Now
                                                <ArrowRight className="ml-2 w-4 h-4" />
                                            </button>
                                        </Link>
                                    ) : (
                                        <button
                                            onClick={handleManageSubscription}
                                            disabled={portalLoading}
                                            className="bg-white hover:bg-slate-50 text-slate-900 px-8 h-12 rounded-xl border border-slate-200 font-bold text-sm transition-all shadow-sm flex items-center justify-center min-w-[180px]"
                                        >
                                            {portalLoading ? <Loader2 className="mr-2 w-4 h-4 animate-spin" /> : <ExternalLink className="mr-2 w-4 h-4" />}
                                            Manage Billing
                                        </button>
                                    )}
                                </div>
                            </div>
                        </motion.div>

                        {/* Info boxes */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {[
                                { Icon: CreditCard, title: 'Secure Checkout', text: 'Encrypted and safe payments.' },
                                { Icon: Calendar, title: 'Flexible Plans', text: 'Cancel or pause at any time.' },
                                { Icon: ShieldCheck, title: 'Direct Support', text: 'Get help from our team.' },
                            ].map(({ Icon, title, text }) => (
                                <div key={title} className="p-8 rounded-3xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                                    <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center mb-6">
                                        <Icon className="w-5 h-5 text-slate-400" />
                                    </div>
                                    <h3 className="text-base font-bold text-slate-900 mb-2">{title}</h3>
                                    <p className="text-xs text-slate-500 font-medium leading-relaxed">{text}</p>
                                </div>
                            ))}
                        </div>

                    </div>
                </main>
            </div>
        </div>
    );
};

export default Billing;
